<template>
  <div class="terms-and-conditions">
    <h2 class="section-title large">
      {{ $t('terms.title') }}
    </h2>
    <p class="subtitle">
      {{ $t('terms.subtitle') }}
    </p>

    <div class="terms-content">
      <section class="content-section intro-section">
        <div class="section-badge">
          {{ $t('terms.aboutNumbersBetting') }}
        </div>
        <p class="intro-text">
          {{ $t('terms.introText') }}
        </p>
      </section>

      <section class="content-section">
        <div class="section-badge">
          {{ $t('terms.availableMarkets') }}
        </div>

        <div class="markets-grid">
          <div
            v-for="market in markets"
            :key="market.key"
            class="market-card"
          >
            <div class="market-card-header">
              <span class="market-icon">{{ market.icon }}</span>
              <h3 class="market-title">
                {{ $t(`terms.${market.title}`) }}
              </h3>
            </div>
            <div class="market-content">
              <p class="market-name">
                {{ $t(`terms.${market.name}`) }}
              </p>
              <div
                v-if="market.colorList"
                class="market-description"
              >
                <p>{{ $t(`terms.${market.desc}`) }}</p>
                <ul>
                  <li
                    v-for="colorKey in bonusColorKeys"
                    :key="colorKey"
                  >
                    {{ $t(`terms.${colorKey}`) }}
                  </li>
                </ul>
              </div>
              <p
                v-else
                class="market-description"
              >
                {{ $t(`terms.${market.desc}`) }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="content-section rules-section">
        <div class="section-badge">
          {{ $t('terms.importantRules') }}
        </div>

        <div class="rules-list">
          <div
            v-for="ruleNumber in rules"
            :key="ruleNumber"
            class="rule-card"
          >
            <div class="rule-number">
              {{ ruleNumber }}
            </div>
            <div class="rule-content">
              <h4 class="rule-title">
                {{ $t(`terms.rule${ruleNumber}Title`) }}
              </h4>
              <p>{{ $t(`terms.rule${ruleNumber}Text`) }}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineOptions } from 'vue'

defineOptions({
  name: 'TermsAndConditionsView',
})

type MarketEntry = {
  key: string
  icon: string
  title: string
  name: string
  desc: string
  colorList?: boolean
}

const markets: MarketEntry[] = [
  { key: 'sumOddEven', icon: '📊', title: 'sumOddEven', name: 'sumOddEvenName', desc: 'sumOddEvenDesc' },
  { key: 'sumOverUnder', icon: '⚖️', title: 'sumOverUnder', name: 'sumOverUnderName', desc: 'sumOverUnderDesc' },
  { key: 'sumWithinRange', icon: '🎯', title: 'sumWithinRange', name: 'sumWithinRangeName', desc: 'sumWithinRangeDesc' },
  { key: 'bonusBallOddEven', icon: '⭐', title: 'bonusBallOddEven', name: 'bonusBallOddEvenName', desc: 'bonusBallOddEvenDesc' },
  { key: 'sumBonusBallsOddEven', icon: '✨', title: 'sumBonusBallsOddEven', name: 'sumBonusBallsOddEvenName', desc: 'sumBonusBallsOddEvenDesc' },
  { key: 'bonusBallOverUnder', icon: '📈', title: 'bonusBallOverUnder', name: 'bonusBallOverUnderName', desc: 'bonusBallOverUnderDesc' },
  { key: 'sumBonusBallsOverUnder', icon: '📉', title: 'sumBonusBallsOverUnder', name: 'sumBonusBallsOverUnderName', desc: 'sumBonusBallsOverUnderDesc' },
  { key: 'bonusBallSingleDigit', icon: '🔢', title: 'bonusBallSingleDigit', name: 'bonusBallSingleDigitName', desc: 'bonusBallSingleDigitDesc' },
  { key: 'bonusBallMatchesRegular', icon: '🔗', title: 'bonusBallMatchesRegular', name: 'bonusBallMatchesRegularName', desc: 'bonusBallMatchesRegularDesc' },
  { key: 'bonusBallWithinRange', icon: '🎲', title: 'bonusBallWithinRange', name: 'bonusBallWithinRangeName', desc: 'bonusBallWithinRangeDesc' },
  { key: 'combinedTotalOverUnder', icon: '➕', title: 'combinedTotalOverUnder', name: 'combinedTotalOverUnderName', desc: 'combinedTotalOverUnderDesc' },
  { key: 'standardBetNoBonus', icon: '🎯', title: 'standardBetNoBonus', name: 'standardBetNoBonusName', desc: 'standardBetNoBonusDesc' },
  { key: 'standardBetSameDrum', icon: '⭐', title: 'standardBetSameDrum', name: 'standardBetSameDrumName', desc: 'standardBetSameDrumDesc' },
  { key: 'standardBetAdditionalDrum', icon: '🎪', title: 'standardBetAdditionalDrum', name: 'standardBetAdditionalDrumName', desc: 'standardBetAdditionalDrumDesc' },
  { key: 'bonusBetSameDrum', icon: '🎲', title: 'bonusBetSameDrum', name: 'bonusBetSameDrumName', desc: 'bonusBetSameDrumDesc' },
  { key: 'bonusBetDifferentDrum', icon: '🎰', title: 'bonusBetDifferentDrum', name: 'bonusBetDifferentDrumName', desc: 'bonusBetDifferentDrumDesc' },
  { key: 'regularPlusBonusSameDrum', icon: '🎯', title: 'regularPlusBonusSameDrum', name: 'regularPlusBonusSameDrumName', desc: 'regularPlusBonusSameDrumDesc' },
  { key: 'ballNotToFall', icon: '🎪', title: 'ballNotToFall', name: 'ballNotToFallName', desc: 'ballNotToFallDesc' },
  { key: 'bonusBallColor', icon: '📊', title: 'bonusBallColor', name: 'bonusBallColorName', desc: 'bonusBallColorDescIntro', colorList: true },
]

const bonusColorKeys = [
  'bonusBallColorListGreen',
  'bonusBallColorListRed',
  'bonusBallColorListOrange',
  'bonusBallColorListYellow',
  'bonusBallColorListBrown',
  'bonusBallColorListPurple',
  'bonusBallColorListBlue',
]

const rules = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
</script>

<style scoped>
.terms-and-conditions {
  max-width: 100%;
  margin: 0 auto;
  padding: 0 10px;
}

.section-title.large {
  color: var(--results-title-color, var(--text-primary));
  font-size: var(--results-title-size, 28px);
  font-weight: var(--results-title-weight, 700);
  margin: 0 0 10px;
}

.subtitle {
  color: var(--results-description-color, var(--text-secondary));
  font-size: var(--results-description-size, 14px);
  line-height: 1.5;
  margin: 0 0 18px;
  text-align: left;
}

.terms-content {
  display: flex;
  flex-direction: column;
  gap: 56px;
}

.content-section {
  position: relative;
}

.section-badge {
  display: inline-block;
  background: var(--accent-color);
  color: var(--text-accent-contrast, white);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 20px;
}

.intro-text {
  font-size: 16px;
  line-height: 1.8;
  color: var(--text-secondary);
  margin: 0;
}

.markets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  margin-top: 32px;
}

.market-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 24px;
  transition: all 0.3s ease;
  cursor: default;
}

.market-card:hover {
  border-color: var(--accent-color);
  box-shadow: 0 4px 16px var(--shadow-color);
  transform: translateY(-2px);
}

.market-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.market-icon {
  font-size: 28px;
  line-height: 1;
}

.market-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.market-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.market-name {
  font-size: 16px;
  font-weight: 500;
  color: var(--accent-color);
  margin: 0;
}

.market-description {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-secondary);
  margin: 0;
}

.market-description ul {
  margin: 8px 0 0;
  padding-left: 20px;
}

.rules-section {
  margin-top: 16px;
}

.rules-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 32px;
}

.rule-card {
  display: flex;
  gap: 20px;
  padding: 24px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.rule-card:hover {
  border-color: var(--accent-color);
  box-shadow: 0 4px 12px var(--shadow-light);
}

.rule-number {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-color);
  color: var(--text-accent-contrast, white);
  border-radius: 8px;
  font-size: 18px;
  font-weight: 700;
}

.rule-content {
  flex: 1;
}

.rule-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.rule-content p {
  font-size: 15px;
  line-height: 1.7;
  color: var(--text-secondary);
  margin: 0;
}

@media (max-width: 768px) {
  .terms-content {
    gap: 40px;
  }

  .intro-text {
    font-size: 15px;
  }

  .markets-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 24px;
  }

  .market-card {
    padding: 20px;
  }

  .market-card-header {
    margin-bottom: 12px;
  }

  .market-icon {
    font-size: 24px;
  }

  .market-title {
    font-size: 16px;
  }

  .market-name {
    font-size: 15px;
  }

  .market-description {
    font-size: 13px;
  }

  .rules-list {
    gap: 16px;
    margin-top: 24px;
  }

  .rule-card {
    flex-direction: column;
    gap: 16px;
    padding: 20px;
  }

  .rule-number {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .rule-title {
    font-size: 16px;
  }

  .rule-content p {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .market-card {
    padding: 16px;
  }

  .rule-card {
    padding: 16px;
  }
}
</style>
